import { maybeGetValidUrl } from "@repo/app-types/helpers/utilities";
import { type FC, Fragment } from "react";

export type AutoLinkSegment =
  | { type: "delimiter"; value: string }
  | { type: "url"; display: string; href: string }
  | { type: "text"; value: string };

/**
 * Parse a string into segments of text, URLs, and delimiters.
 *
 * Splits by comma (preserving the delimiter and surrounding whitespace)
 * to handle comma-separated lists of URLs.
 * This must happen before trying to parse the whole string as a URL,
 * because the URL constructor treats commas as valid path characters
 * (e.g. "https://a.com/1, https://b.com/2" parses as a single URL).
 */
export function parseAutoLinkSegments(
  input: string,
): readonly AutoLinkSegment[] {
  const parts = input.split(/(,\s*)/);

  return parts.map((part, index): AutoLinkSegment => {
    // Odd indices are the captured delimiters (e.g. ", " or ",")
    if (index % 2 === 1) {
      return { type: "delimiter", value: part };
    }
    const trimmed = part.trim();
    const url = maybeGetValidUrl(trimmed);
    if (url) {
      return { type: "url", display: trimmed, href: url.href };
    }
    return { type: "text", value: part };
  });
}

export interface AutoLinkProps {
  children: string | null | undefined;
}

const linkClassName =
  "text-gray-600 underline decoration-gray-400 hover:text-foreground hover:decoration-gray-600";

export const AutoLink: FC<AutoLinkProps> = ({ children }) => {
  if (children === null || children === undefined) {
    return children;
  }

  const segments = parseAutoLinkSegments(children);

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === "url") {
          return (
            <a
              key={index}
              href={segment.href}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClassName}
              title="Open link in new tab"
            >
              {segment.display}
            </a>
          );
        }
        return <Fragment key={index}>{segment.value}</Fragment>;
      })}
    </>
  );
};
